import { useRef, useState, type DragEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Input, Tabs, Textarea } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { relativeTime } from '@/lib/format';
import {
  addVaultGoogleFile, addVaultNote, askVault, deleteVaultSource, getVault, getVaultQuestions, listMeetings, reprocessVaultSource, updateVaultSource, uploadVaultFiles,
} from '@/data/client';
import type { DocLabel, VaultAnswer, VaultSource } from '@/data/types';
import { FormError, Modal } from '@/components/crm/Modal';
import { DOC_TYPE_LABEL, LABEL_OPTIONS, STATUS_LABEL, fileSize } from './labels';
import { SourceViewer } from './SourceViewer';
import { DrivePanel } from './DrivePanel';

/* The Client Vault tab: ask a question, see what's in the Vault, add to it. The
   Vault belongs to the company, so it carries from lead to client; on a deal
   page, transcripts added here are also filed on that deal. */
export function ClientVault({ companyId, companyName, dealId }: { companyId: string; companyName: string; dealId?: string }) {
  const [viewing, setViewing] = useState<{ id: string; highlight: string[] } | null>(null);
  return (
    <div className="crm-list">
      <AskPanel companyId={companyId} companyName={companyName} onOpen={(id, highlight) => setViewing({ id, highlight })} />
      <Sources companyId={companyId} dealId={dealId} onOpen={(id) => setViewing({ id, highlight: [] })} />
      {viewing && <SourceViewer sourceId={viewing.id} highlight={viewing.highlight} onClose={() => setViewing(null)} />}
    </div>
  );
}

function AskPanel({ companyId, companyName, onOpen }: { companyId: string; companyName: string; onOpen: (id: string, highlight: string[]) => void }) {
  const qc = useQueryClient();
  const [question, setQuestion] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const ask = useMutation({
    mutationFn: () => askVault(companyId, question.trim()),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['vaultQuestions', companyId] }),
  });
  const { data: history = [] } = useQuery({ queryKey: ['vaultQuestions', companyId], queryFn: () => getVaultQuestions(companyId), enabled: showHistory });

  return (
    <Card padding={false} className="crm-section vault-ask">
      <div className="crm-section__head">
        <h3><Icon name="lock" size={15} /> Ask {companyName}'s Client Vault</h3>
        <Button variant="ghost" size="sm" onClick={() => setShowHistory((v) => !v)}>{showHistory ? 'Hide' : 'Recent questions'}</Button>
      </div>
      <form className="crm-addopt" onSubmit={(e) => { e.preventDefault(); if (question.trim()) ask.mutate(); }}>
        <Input placeholder="e.g. What did we agree their monthly budget was?" value={question} onChange={(e) => setQuestion(e.target.value)} />
        <Button type="submit" disabled={!question.trim() || ask.isPending} iconLeft={<Icon name="sparkles" size={15} />}>{ask.isPending ? 'Searching…' : 'Ask'}</Button>
      </form>
      <FormError error={ask.error} />
      {ask.data && <Answer answer={ask.data} onOpen={onOpen} />}
      {showHistory && (
        <ul className="vault-history">
          {history.map((h) => (
            <li key={h.id}>
              <strong>{h.question}</strong>
              <span className="crm-cell-sub">{h.askedBy ?? 'Someone'} · {relativeTime(h.askedAt)}</span>
              <Answer answer={h} onOpen={onOpen} compact />
            </li>
          ))}
          {!history.length && <li className="crm-cell-muted">No questions yet.</li>}
        </ul>
      )}
    </Card>
  );
}

/* Cited claims become numbered markers; each opens its document with the
   cited passages highlighted. Markers are numbered per distinct passage set. */
function Answer({ answer, onOpen, compact }: { answer: VaultAnswer; onOpen: (id: string, highlight: string[]) => void; compact?: boolean }) {
  const keys: string[] = [];
  const sources = new Map<string, string>();
  return (
    <div className={`vault-answer ${compact ? 'vault-answer--compact' : ''}`}>
      <p>
        {answer.segments.map((s, i) => (
          <span key={i}>
            {s.text}
            {s.citations.map((c) => {
              const key = `${c.sourceId}|${c.chunkIds.join(',')}`;
              if (!keys.includes(key)) keys.push(key);
              sources.set(c.sourceId, c.title);
              return (
                <button key={key} type="button" className="vault-cite" title={`${c.title}: “${c.citedText.slice(0, 140)}${c.citedText.length > 140 ? '…' : ''}”`}
                  onClick={() => onOpen(c.sourceId, c.chunkIds)}>{keys.indexOf(key) + 1}</button>
              );
            })}
          </span>
        ))}
      </p>
      {sources.size > 0 && !compact && (
        <p className="crm-cell-sub">Sources: {[...sources.entries()].map(([id, title], i) => (
          <span key={id}>{i > 0 && ', '}<button type="button" className="crm-linkbtn" onClick={() => onOpen(id, [])}>{title}</button></span>
        ))}</p>
      )}
    </div>
  );
}

function Sources({ companyId, dealId, onOpen }: { companyId: string; dealId?: string; onOpen: (id: string) => void }) {
  const qc = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [noting, setNoting] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [label, setLabel] = useState<DocLabel | ''>('');
  const [skipped, setSkipped] = useState<{ name: string; reason: string }[]>([]);
  const { data } = useQuery({
    queryKey: ['vault', companyId],
    queryFn: () => getVault(companyId),
    // Poll while anything is still being read.
    refetchInterval: (q) => (q.state.data?.sources.some((s) => s.status === 'queued' || s.status === 'processing') ? 4000 : false),
  });
  const refresh = () => void qc.invalidateQueries({ queryKey: ['vault', companyId] });
  const upload = useMutation({
    mutationFn: (files: File[]) => uploadVaultFiles(companyId, files),
    onSuccess: (r) => { setSkipped(r.skipped); refresh(); },
  });
  const reprocess = useMutation({ mutationFn: reprocessVaultSource, onSuccess: refresh });
  const relabel = useMutation({ mutationFn: (v: { id: string; docType: DocLabel | null }) => updateVaultSource(v.id, { docType: v.docType }), onSuccess: () => {
    refresh(); for (const k of ['company', 'deal']) void qc.invalidateQueries({ queryKey: [k] });
  } });
  const remove = useMutation({ mutationFn: deleteVaultSource, onSuccess: refresh });
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const files = [...e.dataTransfer.files];
    if (files.length) upload.mutate(files);
  };
  const [view, setView] = useState<'documents' | 'email'>('documents');
  const all = data?.sources ?? [];
  const emailCount = all.filter((s) => s.kind === 'email').length;
  const sources = all.filter((s) => (view === 'email' ? s.kind === 'email' : s.kind !== 'email' && (!label || s.docType === label)));

  return (
    <>
    <DrivePanel companyId={companyId} folders={data?.folders ?? []} />
    <Card padding={false}>
      <div className={`crm-section vault-drop ${dragging ? 'is-over' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)} onDrop={onDrop}>
        <div className="crm-section__head" style={{ marginBottom: 6 }}>
          <Tabs items={[{ value: 'documents', label: 'Documents', count: all.length - emailCount }, { value: 'email', label: 'Email', count: emailCount }]}
            value={view} onChange={(v) => setView(v as 'documents' | 'email')} />
          <div className="crm-record__actions">
            {view === 'documents' && (
              <select className="crm-inline-select" aria-label="Show" value={label} onChange={(e) => setLabel(e.target.value as DocLabel | '')}>
                <option value="">All documents</option>
                {LABEL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}s</option>)}
              </select>
            )}
            <Button variant="ghost" size="sm" iconLeft={<Icon name="file-text" size={14} />} onClick={() => setTranscribing(true)}>Add transcript</Button>
            <Button variant="ghost" size="sm" iconLeft={<Icon name="edit" size={14} />} onClick={() => setNoting(true)}>Add note</Button>
            <Button size="sm" iconLeft={<Icon name="upload" size={14} />} onClick={() => fileInput.current?.click()} disabled={upload.isPending}>
              {upload.isPending ? 'Uploading…' : 'Upload files'}
            </Button>
            <input ref={fileInput} type="file" multiple hidden onChange={(e) => { const f = [...(e.target.files ?? [])]; if (f.length) upload.mutate(f); e.target.value = ''; }} />
          </div>
        </div>
        <p className="crm-cell-sub">Drop files here: PDF, Word, Excel, PowerPoint, text. Images, video, archives and design files aren't imported. Passwords are hidden from search and answers; the Vault notes where they're stored.</p>
        {data && !data.aiConfigured && <p className="crm-callout" style={{ marginTop: 10 }}>Documents are stored and searchable, but summaries and Ask need the Anthropic API key on the server.</p>}
        {skipped.length > 0 && <p className="crm-cell-sub" style={{ marginTop: 6 }}>Skipped: {skipped.map((s) => `${s.name} (${s.reason})`).join(', ')}</p>}
        <FormError error={upload.error ?? reprocess.error ?? remove.error ?? relabel.error} />
      </div>
      <table className="dash-table lead-table">
        <thead><tr><th>Document</th><th>Type</th><th>Status</th><th>Updated</th><th /></tr></thead>
        <tbody>
          {sources.map((s) => <SourceRow key={s.id} s={s} onOpen={onOpen} onReprocess={() => reprocess.mutate(s.id)} onRemove={() => remove.mutate(s.id)}
            onLabel={(docType) => relabel.mutate({ id: s.id, docType })} />)}
        </tbody>
      </table>
      {!sources.length && <p className="lead-none">{view === 'email'
        ? 'No client email captured yet. Turn on email capture from Today → Google.'
        : 'The Vault is empty. Upload the client\'s strategy, brand guide, contracts and account notes.'}</p>}
      {noting && <NoteModal companyId={companyId} onClose={() => setNoting(false)} onSaved={refresh} />}
      {transcribing && <TranscriptModal companyId={companyId} dealId={dealId} onClose={() => setTranscribing(false)} onSaved={() => {
        refresh(); for (const k of ['company', 'deal', 'coach']) void qc.invalidateQueries({ queryKey: [k] });
      }} />}
    </Card>
    </>
  );
}

function SourceRow({ s, onOpen, onReprocess, onRemove, onLabel }: {
  s: VaultSource; onOpen: (id: string) => void; onReprocess: () => void; onRemove: () => void; onLabel: (docType: DocLabel | null) => void;
}) {
  const [confirm, setConfirm] = useState(false);
  const busy = s.status === 'queued' || s.status === 'processing';
  return (
    <tr onClick={() => onOpen(s.id)}>
      <td>
        <strong>{s.title}</strong>
        <span className="crm-cell-sub">
          {s.kind === 'drive_file' && s.origin ? <>{s.origin} · </> : null}
          {s.structured?.summary ? s.structured.summary.slice(0, 140) + (s.structured.summary.length > 140 ? '…' : '') : s.kind === 'note' ? 'Note' : fileSize(s.sizeBytes)}
          {s.redactions.length > 0 && <> · <Icon name="lock" size={11} /> {s.redactions.length} credential{s.redactions.length === 1 ? '' : 's'} hidden</>}
        </span>
      </td>
      <td onClick={(e) => e.stopPropagation()}>
        <select className="crm-inline-select" aria-label="Label" value={s.docType ?? ''} onChange={(e) => onLabel((e.target.value || null) as DocLabel | null)}
          title={s.docType ? undefined : 'Label this document'}>
          <option value="">{s.structured ? DOC_TYPE_LABEL[s.structured.docType] ?? 'Document' : 'Unlabelled'}</option>
          {LABEL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {(s.meetingTitle || s.occurredAt) && <span className="crm-cell-sub">{[s.meetingTitle, s.occurredAt && new Date(s.occurredAt).toLocaleDateString()].filter(Boolean).join(' · ')}</span>}
      </td>
      <td>
        <span className={`vault-status vault-status--${s.status}`}>{STATUS_LABEL[s.status]}</span>
        {s.error && <span className="crm-cell-sub" title={s.error}>{s.error.slice(0, 60)}</span>}
      </td>
      <td className="crm-cell-muted">{relativeTime(s.updatedAt)}</td>
      <td className="crm-num" onClick={(e) => e.stopPropagation()}>
        {s.driveFileId && (
          <a className="lead-modal__x" style={{ display: 'inline-flex' }} href={`https://drive.google.com/open?id=${s.driveFileId}`} target="_blank" rel="noreferrer" title="Open in Google Drive" aria-label={`Open ${s.title} in Drive`}>
            <Icon name="external" size={14} />
          </a>
        )}
        {!busy && s.status !== 'unsupported' && (
          <button type="button" className="lead-modal__x" title="Read again" aria-label={`Reprocess ${s.title}`} onClick={onReprocess}><Icon name="refresh-cw" size={14} /></button>
        )}
        <button type="button" className="lead-modal__x" title="Remove from Vault" aria-label={`Remove ${s.title}`} onClick={() => setConfirm(true)}><Icon name="trash" size={14} /></button>
        {confirm && (
          <Modal title={`Remove “${s.title}”?`} onClose={() => setConfirm(false)} footer={<>
            <Button variant="ghost" onClick={() => setConfirm(false)}>Cancel</Button>
            <Button variant="danger" onClick={() => { setConfirm(false); onRemove(); }}>Remove</Button>
          </>}>
            <p>The document, its stored copy and its search index are deleted from the Vault.{s.kind === 'drive_file' ? ' It stays in Google Drive and won’t be re-imported by the folder sync.' : s.kind === 'gdoc' ? ' It stays in Google Drive.' : ''}</p>
          </Modal>
        )}
      </td>
    </tr>
  );
}

function NoteModal({ companyId, onClose, onSaved }: { companyId: string; onClose: () => void; onSaved: () => void }) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [docType, setDocType] = useState<DocLabel | ''>('notes');
  const save = useMutation({ mutationFn: () => addVaultNote(companyId, title.trim(), body.trim(), { docType: docType || null }), onSuccess: () => { onSaved(); onClose(); } });
  return (
    <Modal title="Add a note to the Vault" wide onClose={onClose} footer={<>
      <Button variant="ghost" onClick={onClose}>Cancel</Button>
      <Button onClick={() => save.mutate()} disabled={!title.trim() || !body.trim() || save.isPending}>Save note</Button>
    </>}>
      <div className="crm-form">
        <div className="crm-grid2">
          <Input label="Title" placeholder="e.g. Brand voice notes" value={title} onChange={(e) => setTitle(e.target.value)} />
          <label className="tr-field"><span className="tr-field__label">Label</span>
            <select className="tr-input" value={docType} onChange={(e) => setDocType(e.target.value as DocLabel | '')}>
              {LABEL_OPTIONS.filter((o) => o.value !== 'transcript').map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
        </div>
        <Textarea label="Note" rows={10} value={body} onChange={(e) => setBody(e.target.value)} hint="Markdown headings (## Section) help answers point to the right part." />
        <FormError error={save.error} />
      </div>
    </Modal>
  );
}

type TranscriptFrom = 'paste' | 'file' | 'gdoc';

/* A meeting transcript, however it arrived: pasted (Roam, Otter, Zoom…), a file,
   or a Google Doc. It goes to the Vault labelled as a transcript, on the
   timeline, on the deal, and into the coach's next read. Gemini notes and Roam
   transcripts arrive by themselves; this is for everything else. */
function TranscriptModal({ companyId, dealId, onClose, onSaved }: { companyId: string; dealId?: string; onClose: () => void; onSaved: () => void }) {
  const [from, setFrom] = useState<TranscriptFrom>('paste');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [url, setUrl] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [date, setDate] = useState(new Date().toLocaleDateString('en-CA'));
  const [meetingId, setMeetingId] = useState('');
  const { data: meetings = [] } = useQuery({ queryKey: ['meetings', { companyId }], queryFn: () => listMeetings({ companyId }) });
  const labels = { docType: 'transcript' as const, occurredAt: date || null, meetingId: meetingId || null, dealId: dealId ?? null };
  const save = useMutation({
    mutationFn: async () => {
      if (from === 'paste') return addVaultNote(companyId, title.trim(), body.trim(), labels);
      if (from === 'gdoc') return addVaultGoogleFile(companyId, url.trim(), labels);
      return uploadVaultFiles(companyId, [file!], labels);
    },
    onSuccess: () => { onSaved(); onClose(); },
  });
  const ready = from === 'paste' ? !!title.trim() && !!body.trim() : from === 'gdoc' ? !!url.trim() : !!file;
  return (
    <Modal title="Add a meeting transcript" wide onClose={onClose} footer={<>
      <Button variant="ghost" onClick={onClose}>Cancel</Button>
      <Button onClick={() => save.mutate()} disabled={!ready || save.isPending}>{save.isPending ? 'Saving…' : 'Add transcript'}</Button>
    </>}>
      <div className="crm-form">
        <Tabs items={[{ value: 'paste', label: 'Paste text' }, { value: 'file', label: 'Upload a file' }, { value: 'gdoc', label: 'Google Doc' }]} value={from}
          onChange={(v) => setFrom(v as TranscriptFrom)} />
        <div className="crm-grid2">
          <Input label="Date of the meeting" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          <label className="tr-field"><span className="tr-field__label">Meeting</span>
            <select className="tr-input" value={meetingId} onChange={(e) => setMeetingId(e.target.value)}>
              <option value="">Not a scheduled meeting</option>
              {meetings.map((m) => <option key={m.id} value={m.id}>{m.title} · {new Date(m.startAt).toLocaleDateString()}</option>)}
            </select>
          </label>
        </div>
        {from === 'paste' && <>
          <Input label="Title" placeholder="e.g. Strategy reveal with Dana" value={title} onChange={(e) => setTitle(e.target.value)} />
          <Textarea label="Transcript" rows={12} value={body} onChange={(e) => setBody(e.target.value)} hint="Paste the whole conversation — speaker names help the coach tell who said what." />
        </>}
        {from === 'file' && <input type="file" accept=".txt,.md,.pdf,.doc,.docx,.vtt,.srt" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />}
        {from === 'gdoc' && <Input label="Google Doc link" placeholder="https://docs.google.com/document/d/…" value={url} onChange={(e) => setUrl(e.target.value)}
          hint="Read with your Google access (Drive permission needed)." />}
        <p className="crm-cell-sub">Gemini meeting notes and Roam transcripts arrive automatically — no need to add those.</p>
        <FormError error={save.error} />
      </div>
    </Modal>
  );
}
